import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";

import { ApiError } from "../../api/client.ts";
import { favoriteKey, type FavoriteEntityType } from "../../api/favorites.ts";
import {
  useCancelTrade,
  useCorrectTradeMoments,
  useFillTrade,
  useLookups,
  useMediaUrl,
  useRecalculateTrade,
  useReviewTrade,
  useTrade,
  type CatalogItem,
  type Lookups,
  type RecalculateResult,
  type TradeDetail as Trade,
  type TradeImage,
} from "../../api/hooks.ts";
import {
  badgeOf,
  formatDateTime,
  formatDay,
  formatPercent,
  formatPips,
  formatPrice,
  formatR,
  formatSignedPips,
  labelDirection,
  labelImageKind,
  labelOutcome,
  labelPipsSource,
  labelStatus,
  parseDecimal,
  toneOf,
} from "../../domain/format.ts";
import {
  momentsChangeNothing,
  momentsProblem,
  needsOf,
  toInstant,
  toLocalInput,
  type MomentsDraft,
} from "../../domain/moments.ts";
import { directional, distance } from "../../domain/pips.ts";
import { blankText, idsToSend, reviewChangesNothing } from "../../domain/review.ts";
import { Icon } from "../../ui/Icon.tsx";
import { MomentField, NumberField, TextArea } from "../../ui/Field.tsx";
import { Lightbox, type LightboxShot } from "../../ui/Lightbox.tsx";
import { Modal } from "../../ui/Modal.tsx";
import { SaveButton, useSaveFlash } from "../../ui/SaveButton.tsx";
import { StatusBlock } from "../../ui/StatusBlock.tsx";
import { usePermissions } from "../auth/permissions.ts";
import { CloseTrade } from "./CloseTrade.tsx";
import { TradeImages } from "./TradeImages.tsx";

/**
 * El detalle, y desde aqui los verbos: rellenar, cerrar, cancelar y escribir el
 * relato.
 *
 * El orden es el del propietario (ADR 0007): «quiero ver lo que hice, como
 * estaba el grafico en ese momento mas que el precio». Primero la captura,
 * grande; luego la linea de pips —riesgo, objetivo, resultado— con el R al
 * lado; los precios, plegados; y el relato.
 *
 * Un 409 de concurrencia muestra un aviso y el formulario CONSERVA lo escrito
 * (SPEC 15.3): un usuario con el telefono y el portatil abiertos a la vez es el
 * caso real, y perderle un parrafo por eso seria imperdonable.
 */
export function TradeDetail(): ReactNode {
  const { id } = useParams();
  const trade = useTrade(id);
  const lookups = useLookups();
  const timeZone = lookups.data?.app.timeZone ?? "America/Guatemala";

  return (
    <StatusBlock
      loading={trade.isPending}
      error={trade.error}
      onRetry={() => void trade.refetch()}
    >
      {trade.data !== undefined && (
        <Detail
          trade={trade.data}
          lookups={lookups.data}
          timeZone={timeZone}
          onReload={() => void trade.refetch()}
        />
      )}
    </StatusBlock>
  );
}

function Detail(props: {
  trade: Trade;
  lookups: Lookups | undefined;
  timeZone: string;
  onReload: () => void;
}): ReactNode {
  const { trade, timeZone } = props;
  const digits = trade.pricePrecision;
  const long = trade.direction === "buy";
  const fromPrices = trade.pipsSource === "prices";

  /* Los cuatro verbos de esta pantalla —rellenar, cancelar, cerrar y escribir el
     relato— son escritura. Un Mentor la lee entera y no ve ninguno: el servidor
     le responderia 403, y un boton que siempre falla ensena a desconfiar de la
     aplicacion. ESCONDER NO ES AUTORIZAR. */
  const { canWriteTrades, isAdministrator, viewingOther } = usePermissions();

  /* CORREGIR LAS FECHAS es del administrador y de nadie mas: el propietario lo
     pidio asi -«esto solo para el admin por favor»- y el endpoint exige la misma
     politica que los respaldos. Mirando la bitacora de OTRO no se ofrece, por lo
     mismo que no se ofrece ninguna escritura: el servidor corregiria la propia.
     ESCONDER NO ES AUTORIZAR; esto solo evita un boton que daria 403. */
  const canCorrectMoments = isAdministrator && !viewingOther;
  const [correcting, setCorrecting] = useState(false);

  /* Recalcular vive AQUI y no dentro del aviso de pip obsoleto: al responder,
     el detalle se recarga, `pipSizeIsStale` pasa a falso y ese aviso se
     desmonta. Si la mutacion viviera en el, la respuesta —cuantas se
     recalcularon, cuantas se saltaron y el `note`— desapareceria con el. */
  const recalc = useRecalculateTrade(trade.id);

  /* Los favoritos OFRECIBLES de errores y confluencias, para los chips de la
     revision (ADR 0011). Vienen de `lookups.favorites`, que trae solo los que
     apuntan a algo activo: un chip que ofrece un error archivado ofreceria
     elegir algo que el servidor ya no acepta. */
  const marks = new Set(
    (props.lookups?.favorites ?? []).map((item) => favoriteKey(item.entityType, item.entityId)),
  );

  const favoritesOf = (type: FavoriteEntityType, slug: string): CatalogItem[] =>
    catalogOf(props.lookups, slug).filter((item) => marks.has(favoriteKey(type, item.id)));

  const favoriteMistakes = favoritesOf("mistake", "errores");
  const favoriteConfluences = favoritesOf("confluence", "confluencias");

  return (
    <>
      <div className="sec-h">
        <div>
          <h2>
            <span className="sym" style={{ fontSize: 16, marginRight: 8, verticalAlign: 3 }}>
              {trade.instrumentCode}
            </span>
            {trade.instrumentName}
          </h2>
          <p>
            {trade.entryCode} · {formatDay(trade.tradeDateLocal)} · {labelStatus(trade.status)}
          </p>
        </div>
        <div className="tools">
          <span className={long ? "dir long" : "dir short"}>
            <Icon name={long ? "up" : "down"} />
            {labelDirection(trade.direction)}
          </span>
          {trade.outcome !== null && (
            <span className={`badge ${badgeOf(trade.outcome)}`}>{labelOutcome(trade.outcome)}</span>
          )}
          <Link className="btn ghost sm" to="/trades">
            <Icon name="feed" />
            Bitacora
          </Link>
        </div>
      </div>

      {trade.hasBrokenMandatoryRule && (
        <p className="warnbox" style={{ marginTop: 0, marginBottom: 16 }}>
          <b>Esta operacion rompio una regla innegociable.</b> Es lo unico que la interfaz senala
          por si solo, y mide disciplina, no dinero.
        </p>
      )}

      {/* LA CAPTURA PRIMERO. Es lo que se mira: como estaba el grafico cuando
          entraste y como quedo cuando te toco el stop o el objetivo. */}
      <Hero trade={trade} canWrite={canWriteTrades} />

      {trade.pipsDisagree && <DisagreeNotice trade={trade} />}

      {/* SPEC 13, acotado por el ADR 0007: el tamano de pip solo importa cuando
          los pips SALEN de los precios. Con pips escritos no hay division por la
          que colarse, y recalcular no tocaria nada. */}
      {fromPrices && trade.pipSizeIsStale && (
        <StaleNotice
          canWrite={canWriteTrades}
          busy={recalc.isPending}
          onRecalculate={() => recalc.mutate(trade.version)}
        />
      )}

      {recalc.data !== undefined && <RecalculateOutcome result={recalc.data} />}

      <div className="ledger">
        <Kpi label="Riesgo" value={`${formatPips(trade.plannedRiskPips)} pips`} note="Lo que arriesgaste, el stop" />
        <Kpi label="Objetivo" value={`${formatPips(trade.plannedRewardPips)} pips`} note="Lo que buscabas, el objetivo" />
        <Kpi
          label="Resultado"
          value={trade.resultPips === null ? "—" : `${formatSignedPips(trade.resultPips)} pips`}
          tone={toneOf(trade.resultPips)}
          note={trade.status === "closed" ? "Lo que ganaste o perdiste" : "Todavia sin cerrar"}
        />
        <Kpi label="R" value={formatR(trade.rMultiple)} tone={toneOf(trade.rMultiple)} note="Contra el riesgo planificado" />
        <Kpi label="Captura" value={formatPercent(trade.captureRate)} note="Del recorrido a favor" />
        <Kpi
          label="Adherencia"
          value={formatPercent(trade.adherenceScore)}
          tone={trade.hasBrokenMandatoryRule ? "breach" : undefined}
          note="Reglas del plan cumplidas"
        />
      </div>

      <details className="fold" style={{ marginBottom: 16 }}>
        <summary>
          Precios
          <i className="opt">
            {fromPrices ? "los pips salen de aqui" : "contexto, si los pusiste"}
          </i>
        </summary>
        <div className="fold-b">
          <div className="stat-list">
            <Stat label="Entrada planificada" value={formatPrice(trade.plannedEntryPrice, digits)} />
            <Stat label="Stop" value={formatPrice(trade.stopLossPrice, digits)} />
            <Stat label="Objetivo" value={formatPrice(trade.takeProfitPrice, digits)} />
            <Stat label="Entrada real" value={formatPrice(trade.actualEntryPrice, digits)} />
            <Stat label="Salida real" value={formatPrice(trade.actualExitPrice, digits)} />
            <Stat
              label="Deslizamiento"
              value={trade.slippagePips === null ? "—" : `${formatPips(trade.slippagePips)} pips`}
            />
            <Stat
              label="Tamano de pip usado"
              value={trade.pipSizeUsed === null ? "—" : String(trade.pipSizeUsed)}
            />
            <Stat label="Origen de los pips" value={labelPipsSource(trade.pipsSource)} />
          </div>
        </div>
      </details>

      <div className="grid">
        <section className="panel col-6">
          <div className="panel-h">
            <div>
              <h3>La ejecucion</h3>
              <p>Cuando, y con que nota</p>
            </div>
            {canCorrectMoments ? (
              <button type="button" className="btn ghost sm" onClick={() => setCorrecting(true)}>
                <Icon name="edit" />
                Corregir fechas
              </button>
            ) : (
              <Icon name="clock" />
            )}
          </div>
          <div className="panel-b">
            <div className="stat-list">
              <Stat label="Planificada" value={formatDateTime(trade.plannedAtUtc, timeZone)} />
              <Stat label="Abierta" value={formatDateTime(trade.openedAtUtc, timeZone)} />
              <Stat label="Cerrada" value={formatDateTime(trade.closedAtUtc, timeZone)} />
              <Stat label="Nota del setup" value={grade(trade.setupGrade)} />
              <Stat label="Nota de ejecucion" value={grade(trade.executionGrade)} />
            </div>
          </div>
        </section>

        <section className="panel col-6">
          <div className="panel-h">
            <div>
              <h3>El recorrido</h3>
              <p>Hasta donde fue mientras vivia</p>
            </div>
            <Icon name="shuffle" />
          </div>
          <div className="panel-b">
            <div className="stat-list">
              <Stat
                label="Riesgo-beneficio del plan"
                value={trade.plannedRiskReward === null ? "—" : `1:${trade.plannedRiskReward}`}
              />
              <Stat label="MAE (en contra)" value={pipsOrDash(trade.maePips)} />
              <Stat label="MFE (a favor)" value={pipsOrDash(trade.mfePips)} />
              <Stat label="Captura del recorrido" value={formatPercent(trade.captureRate)} />
            </div>
          </div>
        </section>
      </div>

      {trade.ruleChecks.length > 0 && (
        <section className="panel" style={{ marginTop: 16 }}>
          <div className="panel-h">
            <div>
              <h3>Reglas del plan</h3>
              <p>Adherencia y reglas innegociables son dos senales distintas</p>
            </div>
          </div>
          <div className="panel-b">
            <div className="stat-list">
              {trade.ruleChecks.map((check) => (
                <div key={check.id}>
                  <span>
                    {ruleText(props.lookups, check.planRuleId)}
                    {check.wasMandatory && <span className="tag" style={{ marginLeft: 8 }}>innegociable</span>}
                  </span>
                  <b className={check.isFollowed ? "pill on" : check.wasMandatory ? "badge breach" : "pill off"}>
                    {check.isFollowed ? "Cumplida" : "Rota"}
                  </b>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {canWriteTrades && trade.status === "planned" && <FillPanel trade={trade} />}
      {canWriteTrades && trade.status === "open" && <CloseTrade trade={trade} />}

      {/* La gestion de las capturas —subir, clasificar, borrar— va ANTES del
          relato y no al final: el grafico es lo que se mira mientras se
          escribe que salio bien. Sus miniaturas (`.previews`) no se tocan. */}
      <TradeImages trade={trade} canWrite={canWriteTrades} />

      <Narrative
        trade={trade}
        canWrite={canWriteTrades}
        lookups={props.lookups}
        favoriteMistakes={favoriteMistakes}
        favoriteConfluences={favoriteConfluences}
      />

      {correcting && <MomentsDialog trade={trade} onClose={() => setCorrecting(false)} />}
    </>
  );
}

/**
 * CORREGIR LAS FECHAS DE UNA OPERACION, incluso cerrada. Solo el administrador.
 *
 * Lo pidio el propietario con el caso delante: «a veces la fecha se guarda mal
 * cuando ya esta cerrado el trade y no se puede hacer nada… quisiera poder
 * editarlo para corregir correctamente las publicaciones». Tenia razon en las dos
 * mitades: la hora de entrada solo la ponia el relleno, y el dia de la
 * publicacion se deriva de ella, asi que una cerrada no tenia por donde
 * arreglarse.
 *
 * SOLO LAS FECHAS, y el titulo lo dice. Los precios y los pips de una operacion
 * cerrada no se tocan: reescriben un R ya medido y falsearian la estadistica. Una
 * fecha no entra en ningun calculo —solo decide bajo que dia se lee la
 * operacion—, asi que corregirla ARREGLA el recuento de dos dias en vez de
 * estropear uno.
 *
 * LOS CAMPOS QUE SE PINTAN SALEN DEL ESTADO. Una planificada solo ensena su
 * momento; una cerrada, los tres. Asi la forma que se manda siempre cuadra con el
 * estado, y no hay que explicar un rechazo que no puede ocurrir: corregir una
 * fecha no deshace una transicion.
 */
function MomentsDialog(props: { trade: Trade; onClose: () => void }): ReactNode {
  const { trade } = props;
  const correct = useCorrectTradeMoments(trade.id);
  const needs = needsOf(trade.status);

  const [draft, setDraft] = useState<MomentsDraft>({
    planned: toLocalInput(trade.plannedAtUtc),
    opened: toLocalInput(trade.openedAtUtc),
    closed: toLocalInput(trade.closedAtUtc),
  });

  const error = correct.error instanceof ApiError ? correct.error : null;
  const problem = momentsProblem(draft, needs);
  const unchanged = momentsChangeNothing(draft, trade);
  const set = (part: Partial<MomentsDraft>): void => setDraft({ ...draft, ...part });

  return (
    <Modal
      title="Corregir las fechas"
      narrow
      dirty={!unchanged}
      onClose={props.onClose}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={props.onClose}>
            Cancelar
          </button>
          <SaveButton
            label="Guardar las fechas"
            busy={correct.isPending}
            saved={false}
            disabled={problem !== null || unchanged}
            onClick={() =>
              correct.mutate(
                {
                  plannedAtUtc: toInstant(draft.planned),
                  openedAtUtc: toInstant(draft.opened),
                  closedAtUtc: toInstant(draft.closed),
                  version: trade.version,
                },
                { onSuccess: props.onClose },
              )
            }
          />
        </>
      }
    >
      {error !== null && <Conflict error={error} />}

      <p className="infobox" style={{ marginTop: 0 }}>
        <b>Esto solo mueve las fechas.</b> El resultado, el R y los pips se quedan como estan: una
        fecha no entra en ningun calculo, solo decide bajo que dia sale publicada la operacion.
      </p>

      <MomentField
        label="Planificada"
        value={draft.planned}
        onChange={(value) => set({ planned: value })}
        error={error?.fieldError("plannedAtUtc")}
        hint="Cuando apuntaste el plan."
      />

      {needs.opened && (
        <MomentField
          label="Hora real de entrada"
          value={draft.opened}
          onChange={(value) => set({ opened: value })}
          error={error?.fieldError("openedAtUtc")}
          hint="Es ESTA la que decide el dia de la publicacion."
        />
      )}

      {needs.closed && (
        <MomentField
          label="Hora de cierre"
          value={draft.closed}
          onChange={(value) => set({ closed: value })}
          error={error?.fieldError("closedAtUtc")}
        />
      )}

      {/* La frase que impide guardar, dicha mientras se escribe y no despues de
          un viaje de ida y vuelta. El servidor comprueba lo mismo igual. */}
      {problem !== null && <p className="errbox">{problem}</p>}

      <p className="hint">
        Las horas son las de este dispositivo, igual que al registrar la entrada y el cierre. El dia
        bajo el que sale la publicacion lo calcula el servidor con tu zona de los ajustes.
      </p>
    </Modal>
  );
}

/**
 * La captura, PRIMERO y grande. Si hay un «antes» y un «despues», los dos lado
 * a lado: es literalmente lo que el propietario pidio ver, como estaba el
 * grafico y como quedo cuando le toco el stop o el objetivo. Si no, la primera.
 *
 * Se pinta la variante `view` (1920 px) y no la miniatura: es la pantalla donde
 * se mira el grafico de verdad, y el visor grande carga la misma URL, asi que
 * abrirlo no cuesta una segunda descarga.
 */
function Hero(props: { trade: Trade; canWrite: boolean }): ReactNode {
  const mediaUrl = useMediaUrl();
  const { trade } = props;
  const [shot, setShot] = useState<number | null>(null);

  const images = [...trade.images].sort((left, right) => left.sortOrder - right.sortOrder);

  if (images.length === 0) {
    if (!props.canWrite) return null;

    return (
      <p className="infobox">
        <b>Todavia sin captura.</b> El grafico es la mitad del relato: subela en{" "}
        <b>Capturas</b>, mas abajo, y aqui aparecera grande.
      </p>
    );
  }

  const first = images[0];

  if (first === undefined) return null;

  const before = images.find((image) => image.kind === "before");
  const after = images.find((image) => image.kind === "after");
  const featured: TradeImage[] =
    before !== undefined && after !== undefined ? [before, after] : [first];

  const shots: LightboxShot[] = images.map((image) => ({
    contentHash: image.contentHash,
    width: image.width,
    height: image.height,
    title: image.caption ?? image.originalFileName,
    meta: `${trade.instrumentCode} · ${labelImageKind(image.kind)} · ${formatDay(trade.tradeDateLocal)}`,
    badgeText: trade.outcome === null ? "Abierta" : labelOutcome(trade.outcome),
    note: null,
    badgeTone: badgeOf(trade.outcome),
    tradeId: null,
  }));

  return (
    <>
      <div className={`media hero-media n${featured.length}`}>
        {featured.map((image) => (
          <button
            key={image.id}
            type="button"
            className="thumb"
            aria-label={`Ver en grande: ${labelImageKind(image.kind)}`}
            onClick={() => setShot(images.indexOf(image))}
          >
            <img
              src={mediaUrl(image.contentHash, "view")}
              width={image.width}
              height={image.height}
              alt=""
            />
            <span className="cap">{labelImageKind(image.kind)}</span>
          </button>
        ))}
      </div>

      {shot !== null && (
        <Lightbox shots={shots} index={shot} onIndex={setShot} onClose={() => setShot(null)} />
      )}
    </>
  );
}

/**
 * ADR 0007: los pips escritos y los que salen de los precios no cuadran. Se
 * SENALA en ambar —calidad del dato, como el pip obsoleto— y no se corrige:
 * uno de los dos es un dedazo y decidir cual es cosa del operador.
 *
 * Los derivados se calculan aqui solo para ensenarlos al lado de los escritos;
 * los que valen son los que guardo el servidor.
 */
function DisagreeNotice(props: { trade: Trade }): ReactNode {
  const { trade } = props;
  const pip = trade.pipSizeUsed;
  const direction = trade.direction === "sell" ? "sell" : "buy";

  const derivedRisk =
    pip !== null && trade.plannedEntryPrice !== null && trade.stopLossPrice !== null
      ? distance(trade.plannedEntryPrice, trade.stopLossPrice, pip)
      : null;
  const derivedReward =
    pip !== null && trade.plannedEntryPrice !== null && trade.takeProfitPrice !== null
      ? distance(trade.plannedEntryPrice, trade.takeProfitPrice, pip)
      : null;
  const derivedResult =
    pip !== null && trade.actualEntryPrice !== null && trade.actualExitPrice !== null
      ? directional(trade.actualEntryPrice, trade.actualExitPrice, direction, pip)
      : null;

  return (
    <div className="warnbox" style={{ marginTop: 0, marginBottom: 16 }}>
      <p>
        <b>Los pips escritos y los que salen de los precios no cuadran.</b> Uno de los dos es un
        dedazo. Mandan los escritos —riesgo {formatPips(trade.plannedRiskPips)}, objetivo{" "}
        {formatPips(trade.plannedRewardPips)}
        {trade.resultPips === null ? "" : `, resultado ${formatSignedPips(trade.resultPips)}`}— y de
        los precios plegados salen
        {derivedRisk === null ? "" : ` riesgo ${formatPips(derivedRisk)}`}
        {derivedReward === null ? "" : `, objetivo ${formatPips(derivedReward)}`}
        {derivedResult === null ? "" : `, resultado ${formatSignedPips(derivedResult)}`}. No se
        corrige nada solo: revisa cual esta mal y editalo.
      </p>
    </div>
  );
}

/**
 * SPEC 19.6: el instrumento se corrigio DESPUES de calcular estas metricas. Se
 * senala y se ofrece recalcular; sin esto, el historial queda envenenado en
 * silencio, que es exactamente el fallo que este producto existe para evitar.
 *
 * Solo se ensena con pips DERIVADOS de precios (ADR 0007): con pips escritos el
 * servidor no tocaria nada, y lo dice en `note` si alguna vez llega a pasar.
 */
function StaleNotice(props: {
  canWrite: boolean;
  busy: boolean;
  onRecalculate: () => void;
}): ReactNode {
  return (
    <div className="warnbox" style={{ marginTop: 0, marginBottom: 16 }}>
      <p>
        <b>El tamano de pip del instrumento cambio</b> despues de calcular estas cifras, asi que
        los pips de abajo son los de antes. El R no cambiara: es un ratio.
      </p>
      {props.canWrite && (
        <button
          type="button"
          className="btn ghost sm"
          style={{ marginTop: 10 }}
          disabled={props.busy}
          onClick={props.onRecalculate}
        >
          <Icon name="refresh" />
          {props.busy ? "Recalculando…" : "Recalcular con el pip actual"}
        </button>
      )}
    </div>
  );
}

/**
 * Lo que respondio `POST /recalculate` (200, ADR 0007): cuantas se
 * recalcularon, cuantas se saltaron y, si se salto alguna, el `note` que dice
 * por que. Con pips escritos a mano el servidor no toca nada, y ESO es lo que
 * hay que leer: un boton que no cambia nada y no dice por que ensena a
 * desconfiar de la aplicacion.
 */
function RecalculateOutcome(props: { result: RecalculateResult }): ReactNode {
  const { result } = props;

  return (
    <div className="infobox" style={{ marginBottom: 16 }} role="status">
      <b>
        {result.recalculated > 0
          ? "Pips recalculados con el pip actual del instrumento."
          : "No se recalculo nada."}
      </b>
      <p>
        {result.recalculated} {result.recalculated === 1 ? "recalculada" : "recalculadas"},{" "}
        {result.skipped} {result.skipped === 1 ? "saltada" : "saltadas"}.
        {result.note === null ? "" : ` ${result.note}`}
      </p>
    </div>
  );
}

/**
 * El rellenado: la HORA real de entrada es lo que pasa la operacion a abierta.
 * El precio es opcional (ADR 0007): sin el, el resultado se escribira en pips
 * al cerrar; con el, la app podra contrastarlos.
 */
function FillPanel(props: { trade: Trade }): ReactNode {
  const fill = useFillTrade(props.trade.id);
  const cancel = useCancelTrade(props.trade.id);
  const [price, setPrice] = useState("");
  const [openedAt, setOpenedAt] = useState(localNow());

  const error = fill.error instanceof ApiError ? fill.error : null;

  return (
    <section className="panel" style={{ marginTop: 16 }}>
      <div className="panel-h">
        <div>
          <h3>Registrar la entrada real</h3>
          <p>Sigue planificada hasta que digas a que hora entraste</p>
        </div>
        <Icon name="check" />
      </div>
      <div className="panel-b">
        {error !== null && <Conflict error={error} />}

        <div className="grid2">
          <MomentField label="Hora real de entrada" value={openedAt} onChange={setOpenedAt} />
          <NumberField
            label="Precio real de entrada"
            value={price}
            onChange={setPrice}
            error={error?.fieldError("actualEntryPrice")}
            hint="Opcional. Si lo pones, al cerrar podras escribir el precio de salida ademas de los pips."
          />
        </div>

        <div className="row">
          <SaveButton
            label="Marcar como abierta"
            busy={fill.isPending}
            saved={false}
            disabled={openedAt === ""}
            onClick={() =>
              fill.mutate({
                actualEntryPrice: parseDecimal(price),
                openedAtUtc: new Date(openedAt).toISOString(),
                version: props.trade.version,
              })
            }
          />
          <button
            type="button"
            className="btn danger"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate(props.trade.version)}
          >
            <Icon name="x" />
            No la tome
          </button>
        </div>
        <p className="hint">
          Una planificada que no tomaste tambien dice algo de tu disciplina, asi que se guarda.
        </p>
      </div>
    </section>
  );
}

/**
 * El relato. Un campo vacio significa "no lo toques", nunca "borralo"
 * (SPEC 19.7): la tesis se escribio antes de entrar y es lo que se esta
 * evaluando.
 *
 * De ahi que los TRES en blanco no se puedan guardar: el cuerpo iria con los
 * tres a nulo, el servidor responderia 204 y no cambiaria nada. Es exactamente
 * lo que le paso al propietario, y lo que leyo fue un boton roto. Ahora el boton
 * esta desactivado y dice por que, y cuando si hay algo que guardar confirma con
 * un "Guardado" que se ve.
 */
function Narrative(props: {
  trade: Trade;
  canWrite: boolean;
  lookups: Lookups | undefined;
  favoriteMistakes: readonly CatalogItem[];
  favoriteConfluences: readonly CatalogItem[];
}): ReactNode {
  const review = useReviewTrade(props.trade.id);
  const { saved, flash } = useSaveFlash();
  const [wentWell, setWentWell] = useState("");
  const [toImprove, setToImprove] = useState("");
  const [lesson, setLesson] = useState("");

  /* LOS ERRORES Y LAS CONFLUENCIAS SE ELIGEN AQUI, no en el alta (ADR 0011).
     El propietario lo decidio asi: "mejor las confluencias las pondre en la
     revision ya bien pensadas". Y los errores no podian ir en otro sitio: un
     error se reconoce DESPUES de operar, y pedirlo al entrar seria pedir que se
     declare en que se va a equivocar antes de equivocarse.

     Arrancan con lo que la operacion ya tiene guardado, no vacios: son una
     seleccion que se corrige, no un campo de texto que se anade. */
  /* Se leen de `tags`, que es como el contrato los devuelve: una sola lista con
     su `kind`. El detalle no tiene `mistakeIds` ni `confluenceIds` -- esos dos
     nombres son del CUERPO del PUT, no de la respuesta --, y dar por hecho que
     estaban habria dejado la seleccion siempre vacia: marcar un error y recargar
     lo habria borrado de la pantalla aunque siguiera guardado. */
  const tagsOf = (kind: "mistake" | "confluence"): string[] =>
    props.trade.tags.filter((tag) => tag.kind === kind).map((tag) => tag.catalogItemId);

  const original = {
    mistakeIds: tagsOf("mistake"),
    confluenceIds: tagsOf("confluence"),
  };

  const [mistakeIds, setMistakeIds] = useState<readonly string[]>(original.mistakeIds);
  const [confluenceIds, setConfluenceIds] = useState<readonly string[]>(original.confluenceIds);

  const error = review.error instanceof ApiError ? review.error : null;

  /* La regla vive en `domain/review.ts`, fuera de React, porque es de dominio y
     tiene test de regresion propio. Mira los cinco campos y no solo los tres
     textos: marcar un error es un cambio de verdad, y si esto no lo supiera el
     boton volveria a no hacer nada -- el fallo de 2026-09-11 por otra puerta. */
  const nothing = reviewChangesNothing(
    {
      whatWentWell: wentWell,
      whatToImprove: toImprove,
      lesson,
      mistakeIds,
      confluenceIds,
    },
    original,
  );

  return (
    <section className="panel" style={{ marginTop: 16 }}>
      <div className="panel-h">
        <div>
          <h3>El relato</h3>
          <p>La tesis se juzga contra lo que paso, no al reves</p>
        </div>
        <Icon name="quote" />
      </div>

      <div className="panel-b">
        {props.trade.thesis !== null && props.trade.thesis !== "" && (
          <blockquote className="lesson" style={{ marginTop: 0, marginBottom: 16 }}>
            <b>Tesis, escrita antes de entrar</b>
            <p>{props.trade.thesis}</p>
          </blockquote>
        )}

        {written(props.trade.whatWentWell, "Que salio bien")}
        {written(props.trade.whatToImprove, "Que mejorar")}
        {written(props.trade.lesson, "Leccion")}

        {!props.canWrite && (
          <p className="hint">
            Lo escrito arriba se lee tal cual quedo. Tu rol no escribe en esta bitacora.
          </p>
        )}

        {props.canWrite && (
          <>
            {error !== null && <Conflict error={error} />}

            <div className="grid2">
              <TextArea label="Que salio bien" value={wentWell} onChange={setWentWell} />
              <TextArea label="Que mejorar" value={toImprove} onChange={setToImprove} />
            </div>
            <TextArea
              label="Leccion"
              value={lesson}
              onChange={setLesson}
              placeholder="Se escribe dias despues, cuando ya ves el patron."
              hint="Un campo vacio no borra lo guardado: significa que no lo tocas."
            />

            {/* LOS FAVORITOS PRIMERO Y A UN TOQUE. Con decenas de errores en el
                catalogo, el desplegable es el problema que los chips resuelven:
                se marcan con estrella en Brokers y listas y salen aqui. */}
            <TagPicker
              label="Errores reconocidos"
              hint="Lo que hiciste mal, reconocido despues de operar. Marca y desmarca; vaciarlo borra los que hubiera."
              pool={catalogOf(props.lookups, "errores")}
              favorites={props.favoriteMistakes}
              selected={mistakeIds}
              onToggle={setMistakeIds}
            />

            <TagPicker
              label="Confluencias"
              hint="Lo que de verdad se alineo para entrar. Se piensa mejor aqui que en caliente."
              pool={catalogOf(props.lookups, "confluencias")}
              favorites={props.favoriteConfluences}
              selected={confluenceIds}
              onToggle={setConfluenceIds}
            />

            <SaveButton
              label="Guardar la revision"
              icon="edit"
              busy={review.isPending}
              saved={saved}
              disabled={nothing}
              onClick={() =>
                review.mutate(
                  {
                    whatWentWell: blankText(wentWell),
                    whatToImprove: blankText(toImprove),
                    lesson: blankText(lesson),
                    // Nulo cuando la seleccion no cambio -- "no lo toques" -- y la
                    // lista entera cuando si, vacia incluida, que es la orden de
                    // borrar (SPEC 19.7).
                    mistakeIds: idsToSend(mistakeIds, original.mistakeIds),
                    confluenceIds: idsToSend(confluenceIds, original.confluenceIds),
                    version: props.trade.version,
                  },
                  {
                    onSuccess: () => {
                      setWentWell("");
                      setToImprove("");
                      setLesson("");
                      /* Las etiquetas NO se limpian: son una seleccion que
                         ahora es la guardada, y vaciarlas en pantalla diria que
                         se borraron. Los textos si, porque se acumulan sobre lo
                         ya escrito. */
                      flash();
                    },
                  },
                )
              }
            />

            {nothing && (
              <p className="hint" style={{ marginTop: 8 }}>
                Escribe en alguno de los tres campos para poder guardar. Los tres en blanco
                significan "no toques nada", asi que esa peticion no cambiaria nada.
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function written(value: string | null, label: string): ReactNode {
  if (value === null || value === "") return null;

  return (
    <div className="infobox">
      <b>{label}</b>
      <p>{value}</p>
    </div>
  );
}

/**
 * Un 409 no pierde lo escrito: se avisa y se recarga la version (SPEC 15.3).
 */
export function Conflict(props: { error: ApiError }): ReactNode {
  if (props.error.isConcurrencyConflict) {
    return (
      <p className="warnbox">
        Alguien cambio esta operacion desde otro dispositivo. Lo que escribiste sigue aqui: recarga
        la pagina y vuelve a guardar.
      </p>
    );
  }

  return <p className="errbox">{props.error.message}</p>;
}

function Kpi(props: {
  label: string;
  value: string;
  note: string;
  tone?: string | undefined;
}): ReactNode {
  return (
    <div className="kpi">
      <small>{props.label}</small>
      <b className={props.tone === undefined ? undefined : props.tone}>{props.value}</b>
      <span className="delta">{props.note}</span>
    </div>
  );
}

function Stat(props: { label: string; value: string }): ReactNode {
  return (
    <div>
      <span>{props.label}</span>
      <b>{props.value}</b>
    </div>
  );
}

function pipsOrDash(value: number | null): string {
  return value === null ? "—" : `${formatPips(value)} pips`;
}

const GRADES: Record<string, string> = {
  aPlus: "A+",
  a: "A",
  b: "B",
  c: "C",
  d: "D",
};

function grade(value: string | null): string {
  return value === null ? "—" : (GRADES[value] ?? value);
}

function ruleText(lookups: Lookups | undefined, planRuleId: string): string {
  return lookups?.planRules.find((rule) => rule.id === planRuleId)?.text ?? "Regla del plan";
}

/** `datetime-local` quiere la hora local del dispositivo, sin zona ni segundos. */
export function localNow(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;

  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
}

/**
 * Las etiquetas de la revision: los FAVORITOS a un toque y el resto detras.
 *
 * Es una seleccion multiple, no un desplegable: los errores y las confluencias se
 * marcan varios a la vez y un `<select multiple>` en un telefono es intocable.
 * Los favoritos salen primero porque son los que el propietario reconoce una y
 * otra vez; el resto sigue estando, en su orden, debajo.
 *
 * SIN FAVORITOS NO SE PINTA LA FILA DE ARRIBA, por lo mismo que en el alta: una
 * fila de chips vacia deja un hueco que parece un fallo de carga.
 */
function TagPicker(props: {
  label: string;
  hint: string;
  pool: readonly CatalogItem[];
  favorites: readonly CatalogItem[];
  selected: readonly string[];
  onToggle: (ids: readonly string[]) => void;
}): ReactNode {
  if (props.pool.length === 0) return null;

  const marked = new Set(props.favorites.map((item) => item.id));
  const rest = props.pool.filter((item) => !marked.has(item.id));

  function toggle(id: string): void {
    props.onToggle(
      props.selected.includes(id)
        ? props.selected.filter((other) => other !== id)
        : [...props.selected, id],
    );
  }

  return (
    <div className="fblock" style={{ marginTop: 16 }}>
      <span>{props.label}</span>

      {props.favorites.length > 0 && (
        <div className="favs" role="group" aria-label={`${props.label} favoritos`}>
          {props.favorites.map((item) => (
            <button
              key={item.id}
              type="button"
              className={props.selected.includes(item.id) ? "favchip sel" : "favchip"}
              aria-pressed={props.selected.includes(item.id)}
              onClick={() => toggle(item.id)}
            >
              <Icon name="star" />
              {item.name}
            </button>
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <div className="chips" role="group" aria-label={props.label}>
          {rest.map((item) => (
            <button
              key={item.id}
              type="button"
              className={props.selected.includes(item.id) ? "chip active" : "chip"}
              aria-pressed={props.selected.includes(item.id)}
              onClick={() => toggle(item.id)}
            >
              {item.name}
            </button>
          ))}
        </div>
      )}

      <span className="hint">{props.hint}</span>
    </div>
  );
}

/** Los elementos ACTIVOS de una lista de `lookups`, o vacio mientras no llega. */
function catalogOf(lookups: Lookups | undefined, slug: string): readonly CatalogItem[] {
  return (lookups?.catalogs[slug] ?? []).filter((item) => item.isActive);
}
